import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, Clock, MapPin, Award } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 md:py-20">
        
        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Column 1: Brand & Underwriting details */}
          <div className="lg:col-span-4 space-y-6">
            <Link to="/" className="flex items-center gap-2" data-testid="footer-logo-link">
              <div className="w-8 h-8 rounded bg-white text-slate-900 flex items-center justify-center font-bold text-sm">
                P
              </div>
              <span className="font-bold text-white tracking-tight text-base">
                PRECISION HOME LEADS <span className="text-emerald-500 font-semibold">LLC</span>
              </span>
            </Link>
            
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
              Established B2B digital marketing and qualified homeowner lead generation agency. We connect licensed contracting professionals with local, high-intent homeowners nationwide.
            </p>

            <div className="flex flex-col gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-emerald-500 font-medium">
                <ShieldCheck className="h-4 w-4" />
                SSL Encrypted & Certified Secure
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                <Award className="h-4 w-4 text-slate-400" />
                Verified B2B Merchant Compliant
              </div>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white">Company</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li>
                <Link to="/" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-home">Home</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-about">About Us</Link>
              </li>
              <li>
                <Link to="/how-it-works" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-how-it-works">How It Works</Link>
              </li>
              <li>
                <Link to="/industries" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-industries">Industries We Serve</Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-faq">FAQ</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-emerald-400 transition-colors" data-testid="footer-nav-contact">Contact Support</Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal & Compliance Links */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white">Legal & Policies</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>
                <Link to="/terms-of-service" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-terms-of-service">Terms of Service</Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-terms-conditions">Terms & Conditions</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-privacy">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/consumer-privacy" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-consumer-privacy">Consumer Privacy Notice</Link>
              </li>
              <li>
                <Link to="/refund-policy" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-refund-policy">Refund Policy</Link>
              </li>
              <li>
                <Link to="/cancellation-policy" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-cancellation-policy">Cancellation Policy</Link>
              </li>
              <li>
                <Link to="/sms-terms" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-sms-terms">SMS Terms & Conditions</Link>
              </li>
              <li>
                <Link to="/cookie-policy" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-cookie-policy">Cookie Policy</Link>
              </li>
              <li>
                <Link to="/accessibility" className="hover:text-emerald-400 transition-colors text-xs" data-testid="footer-legal-accessibility">Accessibility Statement</Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact Coordinate details */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white">Corporate HQ</h4>
            <ul className="space-y-3.5 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-xs leading-normal">
                  Precision Home Leads LLC<br />
                  616 Stearn Dr<br />
                  Genoa, IL 60135
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4.5 w-4.5 text-emerald-500 shrink-0" />
                <a href="tel:+14707650187" className="text-xs hover:text-white transition-colors" data-testid="footer-phone">+1 (470) 765-0187</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4.5 w-4.5 text-emerald-500 shrink-0" />
                <a href="mailto:contact@precisionleads.co" className="text-xs hover:text-white transition-colors" data-testid="footer-email">contact@precisionleads.co</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock className="h-4.5 w-4.5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-xs">
                  Mon - Sat: 8:00 AM - 7:00 PM (CST)<br />
                  Sun: Closed
                </span>
              </li>
            </ul>
          </div>

        </div>

        {/* Separator line */}
        <div className="border-t border-slate-800/80 my-10" />

        {/* Compliance and Underwriting Legal Statement */}
        <div className="text-[11px] text-slate-500 space-y-4 leading-relaxed max-w-6xl">
          <p>
            <strong>TCPA Disclosure:</strong> By submitting any lead capture forms or initiating contact, you authorize Precision Home Leads LLC and its local home service contractor network to contact you via telephone, email, and/or text messages (including SMS/MMS) using automated dialing systems, artificial/pre-recorded voice messages, or interactive voice responses at the phone number provided. This consent remains active even if your number is listed on any state or federal Do Not Call registries. You may reply <strong>STOP</strong> at any time to opt-out of future SMS messages, or reply <strong>HELP</strong> for carrier support details. Consent is not a condition for receiving marketing or digital services. Standard carrier messaging and data tariffs may apply.
          </p>
          <p>
            Precision Home Leads LLC is an independent, U.S.-based business-to-business lead generation and digital marketing company. We connect local home improvement contracting firms with residential lead inquiries generated through search engine placement and paid display advertising. We do not provide home contractor services directly and make no specific claims regarding contractor licensing, bond statuses, or residential project outcomes.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800/40 pt-8 mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <div data-testid="footer-copyright">
            © {currentYear} Precision Home Leads LLC. All rights reserved.
          </div>
          <div className="flex gap-4">
            <span className="text-slate-600">Merchant Underwriting Secured</span>
            <span className="text-emerald-600/80">●</span>
            <span className="text-slate-600">SSL 256-Bit Protection</span>
          </div>
        </div>

      </div>
    </footer>
  );
}
